import React, { useEffect, useRef, useState } from 'react';
import api from '../lib/api';
import { dato, idag } from '../lib/format';
import { PageHeader, Card, Tabs } from '../components/ui';
import { Upload, Download, Users, Truck, Package, FileText, FileBarChart, Activity } from 'lucide-react';

const ImportEksport = () => {
  const [tab, setTab] = useState('kunder');
  return (
    <>
      <PageHeader title="Import / eksport" subtitle="CSV-import, SAF-T-eksport og hendelseslogg" />
      <Tabs value={tab} onChange={setTab} items={[
        { key: 'kunder', label: 'Kunder', icon: Users },
        { key: 'leverandorer', label: 'Leverandører', icon: Truck },
        { key: 'produkter', label: 'Produkter', icon: Package },
        { key: 'fakturaer', label: 'Fakturaer', icon: FileText },
        { key: 'eksport', label: 'Eksport SAF-T', icon: FileBarChart },
        { key: 'logg', label: 'Hendelseslogg', icon: Activity },
      ]} />
      {tab === 'kunder' && <CsvImport endpoint="/import/kunder" tittel="Kunder"
        felter="navn, orgnr, epost, adresse, postnr, sted, telefon, peppol_id" />}
      {tab === 'leverandorer' && <CsvImport endpoint="/import/leverandorer" tittel="Leverandører"
        felter="navn, orgnr, epost, adresse, postnr, sted, telefon, peppol_id" />}
      {tab === 'produkter' && <CsvImport endpoint="/import/produkter" tittel="Produkter"
        felter="navn, enhetspris, mva_sats, enhet, beskrivelse, kostpris" />}
      {tab === 'fakturaer' && <CsvImport endpoint="/import/fakturaer" tittel="Fakturaer"
        felter="fakturanummer, kunde_navn, dato, forfall, beskrivelse, antall, enhetspris, mva_sats" />}
      {tab === 'eksport' && <Eksport />}
      {tab === 'logg' && <Hendelseslogg />}
    </>
  );
};

const CsvImport = ({ endpoint, tittel, felter }) => {
  const inputRef = useRef(null);
  const [fil, setFil] = useState(null);
  const [resultat, setResultat] = useState(null);
  const [laster, setLaster] = useState(false);
  const sendInn = async () => {
    if (!fil) { alert('Velg en CSV-fil først.'); return; }
    setLaster(true); setResultat(null);
    const fd = new FormData();
    fd.append('fil', fil);
    try {
      const r = await api.post(endpoint, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setResultat(r.data);
    } catch (e) {
      setResultat({ feil: e?.response?.data?.feil || e.message });
    } finally { setLaster(false); }
  };
  return (
    <Card style={{ padding: 16 }}>
      <h3 style={{ marginTop: 0 }}>Importer {tittel.toLowerCase()} fra CSV</h3>
      <p className="fm-text-muted" style={{ fontSize: 12 }}>
        Forventede kolonner: <code>{felter}</code><br />
        Separator (komma, semikolon eller tab) og tegnsett (UTF-8 / latin-1) detekteres automatisk.
      </p>
      <div className="flex items-center" style={{ gap: 8, margin: '12px 0' }}>
        <input ref={inputRef} type="file" accept=".csv,.txt"
               onChange={(e) => { setFil(e.target.files?.[0] || null); setResultat(null); }} />
        <button className="btn-primary" disabled={!fil || laster} onClick={sendInn}>
          <Upload size={14} /> {laster ? 'Importerer…' : 'Importer'}
        </button>
      </div>
      {resultat && (
        <div style={{ marginTop: 12, padding: 12, borderRadius: 8,
                      background: 'var(--theme-surface-2)' }}>
          {resultat.feil ? (
            <div style={{ color: 'var(--theme-danger)' }}><b>Feil:</b> {resultat.feil}</div>
          ) : (
            <>
              <div><b>Opprettet:</b> {resultat.opprettet ?? 0}</div>
              <div><b>Oppdatert:</b> {resultat.oppdatert ?? 0}</div>
              <div><b>Hoppet over:</b> {resultat.hoppet_over ?? 0}</div>
              {resultat.feilet?.length > 0 && (
                <details style={{ marginTop: 8 }}>
                  <summary>{resultat.feilet.length} feilet</summary>
                  <ul style={{ fontSize: 12 }}>
                    {resultat.feilet.slice(0, 50).map((f, i) => (
                      <li key={i}>Linje {f.linje}: {f.feil}</li>
                    ))}
                  </ul>
                </details>
              )}
            </>
          )}
        </div>
      )}
    </Card>
  );
};

const Eksport = () => {
  const [fra, setFra] = useState(idag().slice(0, 4) + '-01-01');
  const [til, setTil] = useState(idag());
  const lastNed = () => {
    const url = `/api/saf-t?fra=${fra}&til=${til}`;
    window.open(url, '_blank');
  };
  return (
    <Card style={{ padding: 16 }}>
      <h3 style={{ marginTop: 0 }}>Eksporter regnskap (SAF-T XML)</h3>
      <p className="fm-text-muted" style={{ fontSize: 12 }}>
        Standardformat fra Skatteetaten. Brukes ved bokettersyn og overføring mellom regnskapssystemer.
      </p>
      <div className="grid" style={{ gridTemplateColumns: '1fr 1fr', gap: 10, maxWidth: 400 }}>
        <div><label>Fra dato</label>
          <input type="date" value={fra} onChange={(e) => setFra(e.target.value)} /></div>
        <div><label>Til dato</label>
          <input type="date" value={til} onChange={(e) => setTil(e.target.value)} /></div>
      </div>
      <div style={{ marginTop: 12 }}>
        <button className="btn-primary" onClick={lastNed}>
          <Download size={14} /> Last ned SAF-T XML
        </button>
      </div>
    </Card>
  );
};

const Hendelseslogg = () => {
  const [rader, setRader] = useState(null);
  useEffect(() => {
    api.get('/audit', { params: { limit: 500 } }).then((r) => setRader(r.data));
  }, []);
  if (!rader) return <span className="fm-text-muted">Laster…</span>;
  return (
    <Card style={{ padding: 0 }}>
      {rader.length === 0 ? (
        <div style={{ padding: 24, textAlign: 'center' }} className="fm-text-muted">
          Ingen hendelser logget.
        </div>
      ) : (
        <table>
          <thead><tr>
            <th>Tidspunkt</th><th>Bruker</th><th>Handling</th>
            <th>Entitet</th><th>ID</th><th>IP</th><th>Detaljer</th>
          </tr></thead>
          <tbody>
            {rader.map((r) => (
              <tr key={r.id}>
                <td style={{ fontSize: 12 }}>{dato(r.tidspunkt)}</td>
                <td>{r.bruker_id || '—'}</td>
                <td><span className="badge badge-neutral">{r.handling}</span></td>
                <td>{r.entitet || '—'}</td>
                <td className="fm-text-muted" style={{ fontSize: 11 }}>{r.entitet_id || '—'}</td>
                <td className="fm-text-muted" style={{ fontSize: 11 }}>{r.ip || '—'}</td>
                <td style={{ fontSize: 11, maxWidth: 400, overflow: 'hidden',
                             textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {typeof r.detaljer === 'object' ? JSON.stringify(r.detaljer) : (r.detaljer || '')}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Card>
  );
};

export default ImportEksport;
